import { useCallback, useState, type ReactElement } from "react";
import { UserMinus } from "lucide-react";
import { space } from "@tokens/index";
import { Button, Text } from "@primitives/index";
import { DataRow, MutationError } from "../admin-ui";
import { useAddTeamMember, useRemoveTeamMember } from "../management-groups";
import type { Team } from "../management-groups";
import { useUserSearch } from "../queues";
import type { UserSearchResult } from "../queues";
import { UserSearchPicker } from "./UserSearchPicker";
import { resolveUserDisplayName } from "./userDisplayName";

interface TeamMembersEditorProps {
    readonly groupId: string;
    readonly team: Team;
}

export function TeamMembersEditor({ groupId, team }: TeamMembersEditorProps): ReactElement {
    const addMember = useAddTeamMember();
    const removeMember = useRemoveTeamMember();
    const [searchTerm, setSearchTerm] = useState("");
    const userSearch = useUserSearch({ q: searchTerm });

    const handleAdd = useCallback(
        (user: UserSearchResult) => {
            addMember.mutate(
                {
                    id: groupId,
                    teamId: team.id,
                    userId: user.id,
                    displayName: resolveUserDisplayName(user),
                },
                { onSuccess: () => setSearchTerm("") },
            );
        },
        [addMember, groupId, team.id],
    );

    const handleRemove = useCallback(
        (userId: string) => {
            removeMember.mutate({ id: groupId, teamId: team.id, userId });
        },
        [removeMember, groupId, team.id],
    );

    const results = userSearch.data?.results ?? [];
    const alreadyMember = new Set(team.members.map((m) => m.userId));

    return (
        <div style={{ display: "grid", gap: space[2], minWidth: 0 }}>
            {team.members.length === 0 && (
                <Text role="caption" tone="muted">
                    No members yet.
                </Text>
            )}
            {team.members.length > 0 && (
                <div
                    role="list"
                    aria-label={`Members of ${team.name}`}
                    style={{ display: "grid", gap: space[2] }}
                >
                    {team.members.map((m) => (
                        <div role="listitem" key={m.userId}>
                            <DataRow
                                title={m.displayName}
                                meta={m.userId}
                                trailing={
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRemove(m.userId)}
                                        disabled={removeMember.isPending}
                                        aria-label={`Remove ${m.displayName}`}
                                    >
                                        <UserMinus
                                            size={14}
                                            strokeWidth={2.25}
                                            aria-hidden="true"
                                        />
                                        Remove
                                    </Button>
                                }
                            />
                        </div>
                    ))}
                </div>
            )}
            <UserSearchPicker
                label="Search users to add"
                placeholder="Name, email, or user id"
                searchTerm={searchTerm}
                onSearchTermChange={setSearchTerm}
                results={results}
                isFetching={userSearch.isFetching}
                excludedIds={alreadyMember}
                onAdd={handleAdd}
                isAdding={addMember.isPending}
                addLabel="Add"
                excludedLabel="Already member"
            />
            <MutationError error={addMember.error ?? removeMember.error} />
        </div>
    );
}
